/** The Bench view (`#/bench`, UX-SPEC §17): local pass options, deep rewrite entry, the job queue, the Economy toggle. */
import type { ViewModel } from '../../shared/viewmodel.ts';
import { fmtInt } from '../format.ts';
import { result } from '../state.ts';
import { BENCH, JOBS, LATEST, LOCAL_SUMMARY, MESSAGES } from '../strings-bench.ts';
import { BENCH_EXPLAIN } from '../strings-explain.ts';
import { Notice, Seg } from '../ui.tsx';
import { JobsList } from './JobsList.tsx';
import { ChannelIdsIn, TermIn } from './TermIn.tsx';
import { actionPhrase, stepOfMaker } from './action-label.ts';
import { askPending, latestOnScreen, reanalyseLatest } from './chain-control.ts';
import { cancelRun, runLocalPass, setEconomy, startRewrite } from './controller.ts';
import {
	apiKey,
	chain,
	economy,
	economyRound,
	latestN,
	localEffectLines,
	localLog,
	localOptions,
	localResultText,
	parallelState,
	runMessage,
	runProgress,
	stage,
	versionWork,
	versionsAsk,
	viewing,
} from './state.ts';
import { showTranslationOption } from './translation-option.ts';

const TRANSFORMS = ['phrase', 'lex', 'scaffold', 'dash', 'pipe', 'quotes'] as const;

/** The local pass's completion summary (UD-20): how many changes, which substitutions, and the effect per channel. */
function LocalSummary() {
	const log = [...localLog.value].sort((a, b) => b[1] - a[1]);
	const total = log.reduce((n, [, c]) => n + c, 0);
	const shown = log.slice(0, 25);
	const effect = localEffectLines.value;
	return (
		<>
			<p data-testid="bench-local-done">
				<strong>
					{total === 0 ? LOCAL_SUMMARY.none : LOCAL_SUMMARY.done(fmtInt(total), fmtInt(log.length))}
				</strong>
			</p>
			{shown.length > 0 ? (
				<>
					<h3>{LOCAL_SUMMARY.logTitle}</h3>
					<div class="tablewrap">
						<table class="local-log" data-testid="bench-local-log">
							<caption class="visually-hidden">{LOCAL_SUMMARY.logTitle}</caption>
							<thead>
								<tr>
									<th scope="col">{LOCAL_SUMMARY.colWhat}</th>
									<th scope="col" class="num">
										{LOCAL_SUMMARY.colCount}
									</th>
								</tr>
							</thead>
							<tbody>
								{shown.map(([label, n]) => (
									<tr key={label}>
										<td>{label}</td>
										<td class="num">{fmtInt(n)}</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
					{log.length > shown.length ? (
						<p class="dim">{LOCAL_SUMMARY.more(fmtInt(log.length - shown.length))}</p>
					) : null}
				</>
			) : null}
			<h3>{LOCAL_SUMMARY.effectTitle}</h3>
			{effect.length === 0 ? (
				<p class="dim">{LOCAL_SUMMARY.effectNone}</p>
			) : (
				<ul>
					{effect.map((l) => (
						<li key={l}>
							<ChannelIdsIn text={l} />
						</li>
					))}
				</ul>
			)}
			<p class="dim">{LOCAL_SUMMARY.exportHint}</p>
		</>
	);
}

/**
 * Which version the next action works on, whether the views show its own analysis, and "Re-analyse the latest version"
 * (UD-30): a full analysis of the latest version, so every view and the next action's targets come from it.
 */
function NextVersion() {
	const c = chain.value;
	const n = latestN.value;
	const work = versionWork.value;
	// read so the line follows the views (latestOnScreen reads them without subscribing)
	const shown = viewing.value;
	const vm = result.value;
	if (!c || versionsAsk.value !== null || askPending()) return null;
	const last = c.versions.at(-1);
	const after = !last ? '' : actionPhrase(stepOfMaker(last.made));
	const status = work
		? LATEST.viewsMeasuring(String(work.n))
		: vm && latestOnScreen()
			? LATEST.viewsOnIt
			: LATEST.viewsElsewhere(String(shown));
	return (
		<div class="bench-next" data-testid="bench-next-version" data-latest={n}>
			<p>
				<strong data-testid="bench-next-on">{LATEST.nextOn(String(n), after)}</strong>{' '}
				{n > 0 ? <span data-testid="bench-next-status">{status}</span> : null}
			</p>
			{n > 0 ? (
				<div class="bench-actions">
					<div class="bench-action">
						<button
							type="button"
							class="btn btn-secondary"
							data-testid="bench-reanalyse"
							disabled={!!work}
							onClick={() => void reanalyseLatest()}
						>
							{LATEST.reanalyse}
						</button>
						<p class="dim caption">{LATEST.reanalyseHelp}</p>
					</div>
				</div>
			) : null}
		</div>
	);
}

export function BenchView({ vm }: { vm: ViewModel }) {
	const hasTranslation = showTranslationOption(vm.ensemble);
	const s = stage.value;
	return (
		<div class="wrap" data-testid="bench">
			<h1>{BENCH.title}</h1>
			{runMessage.value ? (
				<Notice kind={runMessage.value.kind} live>
					<span data-testid="run-message" data-kind={runMessage.value.kind}>
						{runMessage.value.text}
					</span>
					{runMessage.value.details ? (
						<details class="dim" data-testid="run-message-details">
							<summary>{MESSAGES.detailsLabel}</summary>
							<code>{runMessage.value.details}</code>
						</details>
					) : null}
				</Notice>
			) : null}
			<NextVersion />

			<section aria-labelledby="h-bench-local">
				<h2 id="h-bench-local">
					<TermIn text={BENCH.localTitle} word="Local pass" k="local-pass" />
				</h2>
				<p class="dim">{BENCH.localHelp}</p>
				<Seg label={BENCH.localTitle}>
					{TRANSFORMS.map((t) => (
						<label class="check-label" key={t}>
							<input
								class="check"
								type="checkbox"
								checked={localOptions.value[t]}
								onChange={(e) => {
									localOptions.value = {
										...localOptions.value,
										[t]: (e.target as HTMLInputElement).checked,
									};
								}}
							/>
							<span>{BENCH.transforms[t]}</span>
						</label>
					))}
				</Seg>
				<div class="bench-actions">
					<button
						type="button"
						class="btn btn-primary"
						data-testid="bench-run-local"
						disabled={s === 'local-running'}
						onClick={() => void runLocalPass()}
					>
						{s === 'local-running' ? BENCH.localRunning : BENCH.runLocal}
					</button>
				</div>
				<p class="dim">{BENCH_EXPLAIN.localPass}</p>
				{s === 'local-done' && localResultText.value !== null ? (
					<div data-testid="bench-local-effect">
						<LocalSummary />
					</div>
				) : null}
			</section>

			<section aria-labelledby="h-bench-deep">
				<h2 id="h-bench-deep">
					<TermIn text={BENCH.deepTitle} word="Deep rewrite" k="deep-rewrite" />
				</h2>
				<p class="dim">{BENCH.deepHelp}</p>
				<label class="check-label">
					<input
						class="check"
						type="checkbox"
						checked={economy.value}
						onChange={(e) => setEconomy((e.target as HTMLInputElement).checked)}
					/>
					<span>{BENCH.economyToggle}</span>
				</label>
				<p class="dim">
					<TermIn text={BENCH_EXPLAIN.economy} word="Economy" k="economy" />
				</p>
				{!apiKey.value ? (
					<>
						<p class="dim">{BENCH.enterKeyFirst}</p>
						<p class="dim">{BENCH_EXPLAIN.keyFirst}</p>
					</>
				) : null}
				<div class="bench-actions">
					<div class="bench-action">
						<button
							type="button"
							class="btn btn-secondary"
							onClick={() => startRewrite({ kind: 'flagged' })}
						>
							{BENCH.rewriteFlagged}
						</button>
						<p class="dim caption">{BENCH_EXPLAIN.rewriteFlagged}</p>
					</div>
					<div class="bench-action">
						<button
							type="button"
							class="btn btn-secondary"
							data-testid="bench-rewrite-document"
							onClick={() => startRewrite({ kind: 'document' })}
						>
							{BENCH.rewriteDocument}
						</button>
						<p class="dim caption">
							<TermIn text={BENCH_EXPLAIN.rewriteDocument} word="stretches" k="stretch" />
						</p>
					</div>
					{hasTranslation ? (
						<div class="bench-action">
							<button
								type="button"
								class="btn btn-secondary"
								data-testid="bench-reduce-translation"
								onClick={() => startRewrite({ kind: 'translation' })}
							>
								{BENCH.repairTranslation}
							</button>
							<p class="dim caption">{BENCH_EXPLAIN.repairTranslation}</p>
						</div>
					) : null}
				</div>
				{s === 'running' && runProgress.value ? (
					<div data-testid="bench-progress">
						<p>{BENCH.running(fmtInt(runProgress.value.done), fmtInt(runProgress.value.total))}</p>
						{parallelState.value ? (
							<p class="dim" data-testid="bench-in-flight">
								{BENCH.inFlight(
									fmtInt(parallelState.value.inFlight),
									fmtInt(parallelState.value.limit)
								)}
							</p>
						) : null}
						{economyRound.value?.waiting ? (
							<p class="dim" data-testid="bench-economy-waiting">
								{BENCH.waiting(String(economyRound.value.round))}
							</p>
						) : null}
						<button type="button" class="btn btn-secondary" onClick={cancelRun}>
							{BENCH.cancel}
						</button>
					</div>
				) : null}
				{s === 'done' ? <p data-testid="bench-done">{BENCH.done}</p> : null}
			</section>

			<section aria-labelledby="h-bench-jobs">
				<h2 id="h-bench-jobs">{JOBS.label}</h2>
				<JobsList />
			</section>
		</div>
	);
}
